import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Loading from "../../components/loader";

export const AppointmentCard = ({
  appointment,
  isUpcoming,
  isCancelling,
  setCancellingId,
  onCancel,
}: any) => {
  const [cancelReason, setCancelReason] = useState("");
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("");

  const date = new Date(appointment.bookingStartTime);
  const formatted = date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });

  const closeModal = () => {
    setError("")
    setCancellingId(null);
    setCancelReason("");
  };

  const handleConfirmCancel = async () => {
    if(!cancelReason){
      setError("Please provide a reason for your cancellation")
      return
    }
    setLoading(true)
    await onCancel(appointment?.appointment_id, appointment?.slot_id, cancelReason);
    closeModal();
  };

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="group bg-white rounded-[1rem] p-7 border border-slate-100 shadow-sm hover:shadow-xl hover:border-indigo-100 transition-all duration-500 flex flex-wrap md:flex-nowrap items-center gap-8"
      >
        <div className="relative h-32 w-32 flex-shrink-0">
          <img
            src={appointment.Business?.businessPhotos || "https://via.placeholder.com/150"}
            alt={appointment.Business?.businessName}
            className="h-full w-full object-cover rounded-lg shadow-inner border border-slate-100 transition-transform duration-500 group-hover:scale-105"
          />
        </div>

        <div className="flex-1 flex flex-col gap-4 min-w-[280px]">
          <div>
            <span className="bg-indigo-50 text-indigo-600 text-[10px] font-bold px-3 py-1 rounded-full tracking-wider uppercase border border-indigo-100/30">
              {appointment?.Business?.type?.name || "Service"}
            </span>
          </div>

          <div className="space-y-4">
            <div className="grid gap-2.5">
              <DetailRow label="Service" value={appointment?.LmwServiceType?.name} />
              <DetailRow label="Time" value={`${formatted}`} />
              <DetailRow label="With" value={appointment.Practitioner?.practitionerName} />
              <DetailRow label="At" value={appointment.Business?.businessName} />
              <DetailRow label="Status" value={appointment.status} />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5 min-w-[170px] md:border-l md:border-slate-100 md:pl-8 pt-6 md:pt-0">
          <div>
            <p className="text-base text-slate-400 font-semibold uppercase mb-1">Amount Paid</p>
            <p className="text-xl font-medium text-slate-900 tracking-tighter">${appointment.amount}</p>
          </div>

          <div className="w-full">
            {isUpcoming && (appointment.status === "confirmed" || appointment.status === "pending") ? (
              <button
                onClick={() => setCancellingId(appointment?.appointment_id)}
                className="w-full py-3 rounded-2xl border-2 text-xs font-bold border-red-100 text-red-500 hover:bg-red-50 transition-all duration-300"
              >
                Cancel Booking
              </button>
            ) : (
              <div className="w-full py-3 rounded-2xl bg-slate-50 text-slate-600 text-xs font-semibold uppercase text-center border border-slate-100">
                {appointment?.status}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* --- CANCELLATION MODAL --- */}
      <AnimatePresence>
        {isCancelling && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-slate-600/40"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl border border-slate-100"
            >
              <h3 className="text-xl font-bold text-slate-900 mb-2">Cancel Appointment?</h3>
              <p className="text-slate-600 text-sm mb-6">
                Please let us know why you're cancelling. This helps us improve our service.
              </p>

              <div className="space-y-4">
                <textarea
                  autoFocus
                  placeholder="Reason for cancellation"
                  className="w-full p-4 rounded-2xl border border-slate-200 focus:border-red-300 focus:ring-4 focus:ring-red-50/50 outline-none transition-all resize-none text-slate-700"
                  rows={4}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
                {error && <p className="text-sm text-red-600">{error}</p>}

                {loading ? <Loading size={40} /> : <div className="flex gap-3 pt-2">
                  <button
                    onClick={closeModal}
                    className="flex-1 py-3.5 rounded-2xl bg-slate-100 text-slate-600 font-semibold text-sm hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmCancel}
                    className="flex-[2] py-3.5 rounded-2xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 shadow-lg shadow-red-200 transition-all active:scale-[0.98]"
                  >
                    Confirm
                  </button>
                </div>}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

const DetailRow = ({ label, value }: any) => (
  <div className="flex items-start gap-4">
    <span className="text-sm font-medium text-slate-400 uppercase w-14">{label}</span>
    <span className="text-slate-600 text-sm font-medium">{value || "N/A"}</span>
  </div>
);